import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { getOfficePortalProduct } from "../../api/officePortalApi";
import { formatPoints } from "../Dashboard/templates/DashboardShared";
import { resolvePortalProductGallery } from "./portalProductImages";
import "./DirectorOfficePortal.css";
import "./OfficePortalProductDetail.css";

const formatBdt = (amount) => {
  if (amount == null || amount === "" || !Number.isFinite(Number(amount))) {
    return "Price unavailable";
  }

  return new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    maximumFractionDigits: 2,
  }).format(Number(amount));
};

const formatEarnPoints = (points) => {
  if (points == null) return "—";
  return `${formatPoints(points)} pts`;
};

const OfficePortalProductDetail = () => {
  const { slug, productId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusCode, setStatusCode] = useState(null);
  const [data, setData] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    let active = true;
    const loadProduct = async () => {
      setLoading(true);
      setError("");
      setStatusCode(null);
      setActiveImage(0);
      setData(null);
      try {
        const response = await getOfficePortalProduct(slug, productId);
        if (active) setData(response);
      } catch (err) {
        if (!active) return;
        const status = err?.response?.status;
        setStatusCode(status);
        if (status === 403) {
          setError("You do not have access to this director office portal.");
        } else if (status === 404) {
          setError("This product was not found in the portal catalog.");
        } else {
          setError("Product details are currently unavailable. Please try again later.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (slug && productId) {
      loadProduct();
    }

    return () => {
      active = false;
    };
  }, [slug, productId]);

  if (loading) {
    return (
      <div className="portal-page d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="portal-page">
        <div className="portal-error">
          <h4 className="mb-2">
            {statusCode === 403 ? "Access denied" : statusCode === 404 ? "Not found" : "Product unavailable"}
          </h4>
          <p className="mb-3">{error || "Unable to load product."}</p>
          <Link to={`/office-portal/${slug}`} className="btn btn-outline-primary btn-sm">
            Back to portal
          </Link>
        </div>
      </div>
    );
  }

  const { office, product } = data;
  const gallery = resolvePortalProductGallery(product);
  const inventoryAttributes = product.inventory_attributes || {};
  const displayValue = (label, value) =>
    ["Purpose", "Availability"].includes(label)
      ? String(value).replace(/[_]+/g, " ")
      : String(value);
  const attributeRows = [
    ["Unit", inventoryAttributes.unit_number],
    ["Purpose", inventoryAttributes.purpose],
    ["Availability", product.availability_status || inventoryAttributes.availability_status],
    ["Property ID", inventoryAttributes.property_id],
    ["Property type ID", inventoryAttributes.property_type_id],
    ["Unit ID", inventoryAttributes.unit_id],
  ].filter(([, value]) => value != null && value !== "");
  const serviceName = product.service_name || product.category_name;
  const detailParagraphs = String(product.details || product.description || "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="portal-page portal-detail">
      <nav className="portal-detail__breadcrumb" aria-label="Breadcrumb">
        <Link to={`/office-portal/${slug}`}>← {office?.name || "Office portal"}</Link>
        <span aria-hidden="true">/</span>
        <span>{product.name}</span>
      </nav>

      <div className="portal-detail__layout">
        <section className="portal-detail__gallery">
          <div className="portal-detail__hero-image">
            {gallery.length > 0 ? (
              <img
                src={gallery[activeImage] || gallery[0]}
                alt={product.name}
              />
            ) : (
              <div
                aria-label="No product image available"
                className="portal-detail__image-placeholder"
              />
            )}
          </div>
          {gallery.length > 1 && (
            <div className="portal-detail__thumbs" role="list">
              {gallery.map((url, index) => (
                <button
                  key={`${url}-${index}`}
                  type="button"
                  role="listitem"
                  className={
                    index === activeImage
                      ? "portal-detail__thumb is-active"
                      : "portal-detail__thumb"
                  }
                  onClick={() => setActiveImage(index)}
                  aria-label={`Show photo ${index + 1}`}
                >
                  <img src={url} alt="" />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="portal-detail__info">
          <span className="portal-detail__eyebrow">{office?.company?.name}</span>
          <h1>{product.name}</h1>
          {serviceName && <p className="text-muted">{serviceName}</p>}

          <div className="portal-detail__pricing">
            <div className="portal-card__row">
              <span>Price</span>
              <strong>{formatBdt(product.price_bdt)}</strong>
            </div>
            <div className="portal-card__row">
              <span>Estimated commission</span>
              <span className="portal-card__commission">
                {product.estimated_commission_points == null
                  ? "Unavailable"
                  : formatEarnPoints(product.estimated_commission_points)}
              </span>
            </div>
          </div>

          {attributeRows.length > 0 && (
            <dl className="portal-detail__pricing" aria-label="Product details">
              {attributeRows.map(([label, value]) => (
                <div className="portal-card__row" key={label}>
                  <dt>{label}</dt>
                  <dd className="mb-0">{displayValue(label, value)}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="portal-detail__copy">
            <h2>About this product</h2>
            {detailParagraphs.length ? detailParagraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            )) : <p>Details are not available for this product.</p>}
          </div>

          <Link to={`/office-portal/${slug}`} className="btn btn-outline-secondary">
            Back to all products
          </Link>
        </section>
      </div>
    </div>
  );
};

export default OfficePortalProductDetail;
